import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight, MessageCircle } from "lucide-react";
import { MaskLines } from "./Reveal";
import { SunMark } from "./Logo";
import { Sand } from "./Sand";
import { useLang, CONTACT } from "../i18n";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const { t, lang, wa } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.2]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 80, damping: 15 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 80, damping: 15 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} className="relative h-[100svh] min-h-[640px] overflow-hidden bg-[#0B0A08]" data-testid="hero-section">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <motion.img
          src="/img/moto_r5.jpg" alt="Tour TSM WAY in moto tra le dune del Sahara"
          initial={{ clipPath: "inset(18% 12% 18% 12%)", filter: "brightness(0.3)" }}
          animate={{ clipPath: "inset(0% 0% 0% 0%)", filter: "brightness(0.62)" }}
          transition={{ duration: 1.8, ease }}
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0A08] via-[#0B0A08]/30 to-transparent" />
      <Sand className="z-[5] mix-blend-screen" />
      <div className="absolute inset-0 grain pointer-events-none" />

      <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 900 }} className="hidden md:block absolute right-[8%] top-[22%] w-[260px] lg:w-[340px] pointer-events-none">
        <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.6, delay: 0.6, ease }} className="sun-glow">
          <SunMark className="w-full h-auto drop-shadow-[0_20px_60px_rgba(212,175,55,0.35)]" />
        </motion.div>
      </motion.div>

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 h-full max-w-[1440px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-24 md:pb-28">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.8 }} className="mb-6 flex flex-wrap items-center gap-x-5 gap-y-3">
          <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#F3E5AB]" data-testid="hero-kicker">{t.hero.kicker}</p>
          <a href="#desert-call" className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/50 bg-[#0B0A08]/50 backdrop-blur px-4 py-1.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.25em] text-[#F3E5AB] hover:bg-[#D4AF37] hover:text-[#0B0A08] transition-colors duration-300" data-testid="hero-season-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />{t.hero.season}
          </a>
        </motion.div>
        <h1 key={lang} className="font-display text-[13vw] sm:text-7xl lg:text-[8.5rem] leading-[0.92] tracking-tight text-[#F7F4EE]" data-testid="hero-title">
          <MaskLines lines={t.hero.lines} animateNow delay={0.5} />
        </h1>
        <div className="mt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.3, duration: 1 }} className="max-w-md text-sm sm:text-base text-[#E8E1D3] leading-relaxed">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.5, duration: 0.8 }} className="flex flex-wrap gap-3">
            <a href="#moto" className="btn-gold" data-testid="hero-explore-btn">{t.hero.ctaPrimary} <ArrowDownRight size={16} /></a>
            <a href={wa} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="hero-whatsapp-btn"><MessageCircle size={16} /> {t.hero.ctaSecondary}</a>
          </motion.div>
        </div>
      </motion.div>

      <div className="absolute bottom-6 inset-x-0 z-10 max-w-[1440px] mx-auto px-5 md:px-10 flex justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-[#E6C594]/80">
        <span className="hidden sm:block">{t.hero.coords}</span>
        <span className="flex items-center gap-3">{t.hero.scroll}<span className="scroll-line" /></span>
      </div>
    </section>
  );
};
